import { Grid } from '@mui/material';
import { parseSecondsToHMText } from '@nx-smartmonkey/shared/helpers';
import { RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import More3Points from '../../../../assets/svg/More3Points';
import TotalDistanceIcon from '../../../../assets/svg/Plan/DetailHeader/TotalDistanceIcon';
import TotalDriversIcon from '../../../../assets/svg/Plan/DetailHeader/TotalDriversIcon';
import TotalStopsIcon from '../../../../assets/svg/Plan/DetailHeader/TotalStopsIcon';
import TotalTimeIcon from '../../../../assets/svg/Plan/DetailHeader/TotalTimeIcon';
import TotalUnassignedStopsIcon from '../../../../assets/svg/Plan/DetailHeader/TotalUnassignedStopsIcon';
import { PlanActionsMenu } from '../../../../components/menus/PlanActionsMenu';
import * as routing from '../../../../constants/Routing';
import { Driver } from '../../../../domain/driver/Driver';
import { kmToSM } from '../../../../helpers/Units';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useUpdateActivePlan } from '../../../../hooks/plan/useUpdateActivePlan';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import { PlanHeaderActions } from './PlanHeaderActions';
import { PlanTabs } from './PlanTabs';

export const PlanHeader = () => {
  const navigate = useNavigate();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: stops } = useRetrieveActivePlanStops();
  const [hoverName, setHoverName] = useState<boolean>(false);
  const { run: updateActivePlan } = useUpdateActivePlan();
  const { data: activePlan, isLoading } = useRetrieveActivePlan();

  /**
   * Total duration is the amount of the difference between planned_end_time and planned_start_time of each driver.
   */
  const getRoutesTotalDuration = (): number => {
    let totalDuration = 0;
    (drivers ?? []).forEach((driver: Driver) => {
      totalDuration += (driver.planned_end_time ?? 0) - (driver.planned_start_time ?? 0);
    });
    return totalDuration;
  };

  const getPlanTotalAssignedStops = (): number => {
    return (drivers ?? []).reduce((amount: number, driver: Driver) => amount + driver.stops.length, 0);
  };

  /**
   * Total distance is the amount of distance_to_next_stop of each
   * driver stop and the first driver stop distance_to_previous_stop.
   */
  const getPlanTotalDistance = (): string => {
    let totalDistance = 0;
    (drivers ?? []).forEach((driver: Driver) => {
      if (driver.stops.length > 0) {
        const distanceToFirstService = driver.stops[0].distance_to_previous_stop ?? 0;
        totalDistance +=
          (driver.stops.reduce((ps: any, ns: any) => ps + (ns.distance_to_next_stop || 0), distanceToFirstService) ||
            0) / 1000;
      }
    });
    return `${
      kmToSM(totalDistance, activeProject?.units?.distance) > 100
        ? Math.floor(kmToSM(totalDistance, activeProject?.units?.distance))
        : kmToSM(totalDistance, activeProject?.units?.distance).toFixed(1)
    }${activeProject?.units?.distance}`;
  };

  const canShowPlanActions = (): boolean => {
    if (!activePlan) return false;
    return hoverName;
  };

  return (
    <Grid
      wrap="nowrap"
      container
      direction="row"
      style={{
        height: `62px`,
        borderBottom: `1px solid ${RoutalPalette.neutral10}`,
        backgroundColor: RoutalPalette.white,
      }}
    >
      <Grid
        item
        container
        direction="row"
        style={{
          position: `relative`,
          width: `380px`,
          height: `62px`,
          padding: `0 8px`,
          borderRight: `1px solid ${RoutalPalette.neutral10}`,
        }}
        wrap="nowrap"
        alignItems="center"
        onMouseEnter={() => setHoverName(true)}
        onMouseLeave={() => setHoverName(false)}
      >
        {isLoading ? (
          <Grid item container direction="column">
            <RoutalLoader width={22} />
          </Grid>
        ) : (
          <Grid item container direction="column">
            <Grid item container direction="row" alignItems="center" wrap="nowrap">
              <InputText
                fieldValue={activePlan?.label ?? ``}
                handleEnter={(value) => {
                  updateActivePlan({ label: value });
                }}
                forceFocusedBorderColor
                focusedBorderColor={RoutalPalette.secondary60}
                inputStyles={{ fontSize: `16px`, fontWeight: `bold` }}
                inputContainerStyles={{
                  marginBottom: `2px`,
                  padding: 0,
                  alignContent: `center`,
                  width: `calc(380px - 40px)`,
                }}
                variant={`clean`}
                bGColor={`transparent`}
                hoverBGColor={RoutalPalette.neutral05}
                inputProps={{
                  style: {
                    fontSize: `16px`,
                    fontWeight: 500,
                    height: `18px`,
                    overflow: `hidden`,
                    color: RoutalPalette.primary80,
                  },
                }}
              />
            </Grid>

            <Grid item container direction="row" wrap="nowrap" justifyContent="space-between">
              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalTimeIcon />
                <Text style={{ marginLeft: `4px` }}>{parseSecondsToHMText(getRoutesTotalDuration())}</Text>
              </Grid>

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalDistanceIcon />
                <Text style={{ marginLeft: `4px` }}>{getPlanTotalDistance()}</Text>
              </Grid>

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalStopsIcon />
                <Text style={{ marginLeft: `4px` }}>{getPlanTotalAssignedStops()}</Text>
              </Grid>

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalUnassignedStopsIcon />
                <Text style={{ marginLeft: `4px` }}>
                  {(stops ?? []).filter((stop) => stop.driver_id === undefined).length}
                </Text>
              </Grid>

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
                <TotalDriversIcon />
                <Text style={{ marginLeft: `4px` }}>{(drivers ?? []).length}</Text>
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid
          container
          style={{
            visibility: canShowPlanActions() ? `visible` : `hidden`,
            position: `absolute`,
            width: `20px`,
            height: `calc(100% - 2px)`,
            top: 0,
            right: `4px`,
            borderRadius: `0 2px 2px 0`,
          }}
          justifyContent="center"
          alignContent="center"
        >
          <PlanActionsMenu
            type="popover"
            plans={[activePlan!]}
            showOpenPlan={false}
            setContextAnchor={() => {
              setHoverName(false);
            }}
            onDeletePlan={() => {
              navigate(routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id));
            }}
            popoverProps={{
              hideArrow: true,
              content: <More3Points />,
              style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
          />
        </Grid>
      </Grid>

      <Grid item container wrap="nowrap" style={{ flex: 1 }}>
        <Grid container style={{ width: `100%` }}>
          <PlanTabs activeProject={activeProject} activePlan={activePlan} />
        </Grid>
        <Grid container justifyContent="center" style={{ maxWidth: `463px`, flexGrow: 1 }}>
          <Grid
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            justifyContent="center"
            style={{ width: `fit-content`, padding: `0 4px` }}
          >
            <PlanHeaderActions />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
